import { BaseNode, type NodeConfig } from 'mflows';

// WebSocket节点配置类型
interface WebSocketClientConfig extends NodeConfig {
  url: string;
  protocols?: string[];
  reconnectInterval?: number;
  maxReconnectAttempts?: number;
}

// WebSocket连接状态数据类型
interface WebSocketData {
  status: 'connecting' | 'connected' | 'disconnected' | 'error';
  url?: string;
  message?: string;
  timestamp: number;
  error?: string;
  data?: any;
}

export class WebSocketClient extends BaseNode<WebSocketClientConfig, WebSocketData> {
  static NodeType = 'WebSocket客户端';

  private socket?: WebSocket;
  private reconnectAttempts: number = 0;
  private reconnectTimer?: NodeJS.Timeout;

  protected async initialize(): Promise<void> {
    // 定义输入连接点
    this.setInputPorts([
      { id: 'connect', name: '连接', allowTypes: ['flow'], dataType: 'flow' },
      { id: 'send', name: '发送', allowTypes: ['any'], dataType: 'any' },
      { id: 'disconnect', name: '断开', allowTypes: ['flow'], dataType: 'flow' }
    ]);

    this.setOutputPorts([
      { id: 'message', name: '消息', dataType: 'any' },
      { id: 'status', name: '状态', dataType: 'object' },
      { id: 'error', name: '错误', dataType: 'object' },
      { id: 'connected', name: '已连接', dataType: 'flow' },
      { id: 'disconnected', name: '已断开', dataType: 'flow' }
    ]);
  }

  protected receiveData(pointId: string, data: unknown): void {
    console.log('WebSocketClient receiveData', pointId, data);
    
    switch (pointId) {
      case 'connect':
        this.connect();
        break;
      case 'send':
        this.sendMessage(data);
        break;
      case 'disconnect':
        this.disconnect();
        break;
    }
  }

  protected checkConfig(): boolean {
    return Boolean(this.settings.url) && this.isValidWebSocketUrl(this.settings.url);
  }

  protected async processConfigUpdate(settings: WebSocketClientConfig): Promise<WebSocketData> {
    // 配置更新时，如果连接存在则重新连接
    if (this.socket && this.socket.readyState === WebSocket.OPEN) {
      this.disconnect();
      setTimeout(() => this.connect(), 100);
    }
    
    return {
      status: 'disconnected',
      timestamp: Date.now(),
      message: '配置已更新'
    };
  }

  private isValidWebSocketUrl(url: string): boolean {
    try {
      const parsed = new URL(url);
      return parsed.protocol === 'ws:' || parsed.protocol === 'wss:';
    } catch {
      return false;
    }
  }

  private async connect(): Promise<void> {
    if (!this.checkConfig()) {
      await this.emitStatus('error', '无效的WebSocket URL');
      return;
    }

    if (this.socket && (this.socket.readyState === WebSocket.CONNECTING || this.socket.readyState === WebSocket.OPEN)) {
      return; // 已经在连接或已连接
    }

    try {
      await this.emitStatus('connecting', '正在连接...');
      
      this.socket = new WebSocket(this.settings.url, this.settings.protocols);
      
      this.socket.onopen = () => {
        this.reconnectAttempts = 0;
        this.emitStatus('connected', '连接成功');
      };
      
      this.socket.onmessage = (event) => {
        this.emitMessage(event.data);
      };
      
      this.socket.onclose = (event) => {
        this.emitStatus('disconnected', `连接关闭: ${event.code} ${event.reason}`);
        this.attemptReconnect();
      };
      
      this.socket.onerror = (event) => {
        this.emitStatus('error', `连接错误: ${event}`);
      };
      
    } catch (error) {
      await this.emitStatus('error', `连接失败: ${error}`);
    }
  }

  private disconnect(): void {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = undefined;
    }
    
    if (this.socket) {
      this.socket.close();
      this.socket = undefined;
    }
  }

  private sendMessage(data: unknown): void {
    if (!this.socket || this.socket.readyState !== WebSocket.OPEN) {
      this.emitStatus('error', 'WebSocket未连接');
      return;
    }

    try {
      const message = typeof data === 'string' ? data : JSON.stringify(data);
      this.socket.send(message);
    } catch (error) {
      this.emitStatus('error', `发送消息失败: ${error}`);
    }
  }

  private attemptReconnect(): void {
    const maxAttempts = this.settings.maxReconnectAttempts || 5;
    const interval = this.settings.reconnectInterval || 3000;
    
    if (this.reconnectAttempts >= maxAttempts) {
      this.emitStatus('error', '达到最大重连次数');
      return;
    }
    
    this.reconnectAttempts++;
    this.reconnectTimer = setTimeout(() => {
      this.emitStatus('connecting', `重连中... (${this.reconnectAttempts}/${maxAttempts})`);
      this.connect();
    }, interval);
  }

  private async emitStatus(status: WebSocketData['status'], message: string): Promise<void> {
    const data: WebSocketData = {
      status,
      url: this.settings.url,
      message,
      timestamp: Date.now()
    };
    
    await this.setDataChange(data);
  }

  private async emitMessage(messageData: any): Promise<void> {
    let parsedData = messageData;
    try {
      parsedData = JSON.parse(messageData);
    } catch {
      // 保持原始数据
    }
    
    const data: WebSocketData = {
      status: 'connected',
      url: this.settings.url,
      data: parsedData,
      timestamp: Date.now()
    };
    
    await this.setDataChange(data);
  }

  // 覆盖输出端口行为：根据状态分发到不同端口
  protected getOutputPortActions(): { dataOnly?: string[]; triggerFlow?: string[] } {
    const state = this.getState();
    const data = this.getValue();
    
    if (state === 'error') {
      return { dataOnly: ['error'], triggerFlow: [] };
    }
    
    if (data?.status === 'connected' && data?.data !== undefined) {
      // 收到消息时：发送到 message 端口
      return { dataOnly: ['message', 'status'], triggerFlow: [] };
    }
    
    if (data?.status === 'connected') {
      // 连接成功：触发 connected 流控端口
      return { dataOnly: ['status'], triggerFlow: ['connected'] };
    }
    
    if (data?.status === 'disconnected') {
      // 断开连接：触发 disconnected 流控端口
      return { dataOnly: ['status'], triggerFlow: ['disconnected'] };
    }
    
    // 其他状态（connecting）：仅发送状态
    return { dataOnly: ['status'], triggerFlow: [] };
  }

  public destroy(): void {
    this.disconnect();
    super.destroy();
  }
}